import type { QueryParamsInterface } from '../../../types/Shared';

type TemplateType = 'task' | 'milestone' | 'deliverable';

interface LegacyTdmPayload {
  resourceType?: string;
  type?: string;
  templateData?: unknown;
  templateTdm?: unknown;
  templateTdms?: unknown;
}

const templateTypeMap: Record<string, TemplateType> = {
  TASK: 'task',
  task: 'task',
  MILESTONE: 'milestone',
  milestone: 'milestone',
  DELIVERABLE: 'deliverable',
  deliverable: 'deliverable',
};

export function normalizeTemplateType(type: unknown): TemplateType | undefined {
  return typeof type === 'string' ? templateTypeMap[type] : undefined;
}

function omitUndefined(params: QueryParamsInterface): QueryParamsInterface {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined)) as QueryParamsInterface;
}

export function normalizeProjectTemplateListParams(params?: QueryParamsInterface): QueryParamsInterface | undefined {
  if (!params) return undefined;

  const { perPage, ...rest } = params;
  return omitUndefined({
    ...rest,
    pageSize: rest.pageSize ?? perPage,
  });
}

export function normalizeProjectTemplateTdmParams(params?: QueryParamsInterface): QueryParamsInterface | undefined {
  if (!params) return undefined;

  const { perPage, resourceType, order, orderBy, ...rest } = params;
  return omitUndefined({
    ...rest,
    type: normalizeTemplateType(rest.type) ?? normalizeTemplateType(resourceType),
    pageSize: rest.pageSize ?? perPage,
  });
}

export function normalizeSingleTdmPayload(data: LegacyTdmPayload): Record<string, unknown> {
  const type = normalizeTemplateType(data.type) ?? normalizeTemplateType(data.resourceType);
  const templateData = (data.templateData ?? data.templateTdm ?? {}) as Record<string, unknown>;

  return {
    ...templateData,
    ...(type && { type }),
  };
}

export function normalizeDeleteTdmParams(data: { resourceType?: string; type?: string }): QueryParamsInterface {
  return omitUndefined({
    type: normalizeTemplateType(data.type) ?? normalizeTemplateType(data.resourceType),
  });
}

export function normalizeBulkTdmPayload(data: LegacyTdmPayload | LegacyTdmPayload[]): { tdms: Record<string, unknown>[] } {
  const groups = Array.isArray(data) ? data : [data];
  const tdms = groups.flatMap((group) => {
    const type = normalizeTemplateType(group.type) ?? normalizeTemplateType(group.resourceType);
    const items = Array.isArray(group.templateTdms) ? group.templateTdms : group.templateTdms ? [group.templateTdms] : [];

    return items.map((item) => ({
      type,
      data: item as Record<string, unknown>,
    }));
  });

  return { tdms };
}

export function normalizeTdmSortPayload(data: LegacyTdmPayload): { type?: TemplateType; updates: Record<string, unknown>[] } {
  const updates = Array.isArray(data.templateTdms) ? data.templateTdms : data.templateTdms ? [data.templateTdms] : [];

  return {
    type: normalizeTemplateType(data.type) ?? normalizeTemplateType(data.resourceType),
    updates: updates as Record<string, unknown>[],
  };
}
